import { AnimationEvent } from "@angular/animations";

import { CdkTrapFocus } from "@angular/cdk/a11y";
import { NgClass } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    HostListener,
    inject,
    Injector,
    OnDestroy,
    OnInit,
    Renderer2,
    signal,
    Type,
    ViewChild,
    ViewContainerRef,
    WritableSignal,
    DOCUMENT,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Subscription } from "rxjs";
import { windowScrollTo } from "seamless-scroll-polyfill";

import { exportedSass } from "@hermes/aphrodite-design-system-tools-exported-sass";
import { LayoutFacade } from "@hermes/aphrodite-layout-state";
import { Context } from "@hermes/core-app-core";
import { stopEventPropagation } from "@hermes/utils-generic-helpers";
import { BreakpointService } from "@hermes/utils-generic-services-user-interface";

import { TrayData, TrayPosition } from "../../models/tray-data.model";
import { TrayAnalyticsService } from "../../services/tray-analytics.service";
import { TrayStackService } from "../../services/tray-stack.service";
import { CategoryInstitutionalComponent } from "../category-institutional/category-institutional.component";
import { TrayContentComponent } from "../tray-content/tray-content.component";
import { TrayOverlayComponent } from "../tray-overlay/tray-overlay.component";
import { TrayTitleComponent } from "../tray-title/tray-title.component";

import { TrayContainer } from "./tray-container";
import { trayContainerAnimations } from "./tray-container.animations";

/**
 * This components manages the tray and its animations.
 * It displays opened trays and hides when the stack is empty..
 */
@Component({
    selector: "h-tray-container",
    imports: [
        NgClass,
        CdkTrapFocus,
        TrayTitleComponent,
        TrayContentComponent,
        CategoryInstitutionalComponent,
        TrayOverlayComponent,
    ],
    templateUrl: "./tray-container.component.html",
    styleUrls: ["./tray-container.component.scss"],
    providers: [
        { provide: TrayContainer, useExisting: TrayContainerComponent },
    ],
    animations: trayContainerAnimations,
})
export class TrayContainerComponent
    implements OnInit, OnDestroy, TrayContainer
{
    private document = inject(DOCUMENT);
    private trayStack = inject(TrayStackService);
    private breakpointService = inject(BreakpointService);
    private renderer = inject(Renderer2);
    private context = inject(Context);
    private changeDetector = inject(ChangeDetectorRef);
    public layoutFacade = inject(LayoutFacade);
    public trayAnalyticsService = inject(TrayAnalyticsService);

    public isForceTrapFocus: WritableSignal<boolean> = signal(false);

    @ViewChild("overlayMapContainer", { read: ViewContainerRef, static: true })
    protected overlayMapContainerRef!: ViewContainerRef;

    /**
     * List of trays (title and content) to display.
     */
    public trays: TrayData[] = [];

    /**
     * true if the tray container is open
     */
    public isOpen: boolean = false;

    /**
     * true if the overlay needs to be shown
     */
    public hasOverlay: boolean = false;

    /**
     * true if the footer "Category Institutional" needs to be shown
     */
    public hasCategoryInstitutional: boolean = false;

    /**
     * "left" or "right"
     */
    public position: TrayPosition = "right";

    /**
     * Indicate if we show the container (linked to isOpen, but delayed after the animations)
     */
    public showContainer: boolean = false;

    public noPaddingBottom: boolean = false;

    public noPaddingHorizontal: boolean = false;

    public defaultTrayColor = exportedSass.miscellanousColors["color-white"];

    /**
     * subscription to the trayStack observable
     */
    private subscriptions: Subscription = new Subscription();

    private scrollTop: number = 0;

    public isCookiePopinVisible = toSignal(
        this.layoutFacade.isCookiePopinVisible$,
    );

    /**
     * Close the tray.
     */
    public closeTray(event: Event): void {
        const lastTrayName = this.trays.at(-1)?.name;
        if (lastTrayName) {
            this.trayAnalyticsService.sendUserClosingTrayEvent(lastTrayName);
        }
        this.stopPropagation(event);
        this.destroyMapOverlay();
        this.trayStack.popTray();
    }

    @HostListener("document:keydown.escape", ["$event"])
    public closeTrayWithKeydownEscape(event: Event): void {
        if (!this.isCookiePopinVisible()) {
            this.closeTray(event);
        }
    }

    public ngOnInit(): void {
        this.subscriptions.add(
            this.trayStack.trays$.subscribe((value: TrayData[]) => {
                this.trays = value;
                this.deriveContainerData(this.trays);
                this.changeDetector.detectChanges();
                this.handleBodyScroll();
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public forceTrapFocus(): void {
        this.isForceTrapFocus.set(true);
    }

    /**
     * This is needed for performance of the *ngFor and to keep old trays in the dom.
     * Without it, angular compares array content by reference and recreates all the trays.
     */
    public trackByUuid(_index: number, item: TrayData): string {
        return item.uuid;
    }

    /**
     * Prevents the scroll to go to the page.
     */
    public stopPropagation(event: Event): void {
        stopEventPropagation(event);
        event.stopImmediatePropagation();
    }

    /**
     * Sets parameters of the tray from the last in the stack.
     */
    public deriveContainerData(data: TrayData[]): void {
        if (!data || data.length === 0) {
            this.isOpen = false;
            this.hasOverlay = false;
            this.position = "right";
            return;
        }

        const lastTray: TrayData = data[data.length - 1];

        this.hasOverlay = Boolean(lastTray.hasOverlay);
        this.isOpen = Boolean(lastTray.isOpen);
        this.position = lastTray.position ?? "right";
        this.noPaddingBottom = Boolean(lastTray.noPaddingBottom);
        this.noPaddingHorizontal = Boolean(lastTray.noPaddingHorizontal);
    }

    public handleOverlayClick(): void {
        this.trayStack.popTray();
    }

    public onAnimationStart(): void {
        this.showContainer = true;
    }

    public onAnimationEnd(event: AnimationEvent): void {
        if (event.toState === "void") {
            this.showContainer = false;
        }
    }

    public attachMapOverlay(
        componentType: Type<unknown>,
        options: { injector?: Injector } = {},
    ) {
        this.overlayMapContainerRef.createComponent(componentType, options);
    }

    public destroyMapOverlay() {
        this.overlayMapContainerRef.clear();
    }

    /**
     * Handles body scroll locking.
     * On iOS, setting 'overflow: hidden' alone does not fully lock the body scroll,
     * so this function uses a fixed position with an offset on the body element.
     */
    private handleBodyScroll(): void {
        if (
            this.breakpointService.mediumBreakpointMatcher() ||
            this.context.isInServerMode() ||
            (this.trays.length > 0 && this.trays[0].preventScrollOnClose)
        ) {
            return;
        }

        if (this.trays.length === 1) {
            this.scrollTop = window.scrollY;

            // apply the fixed position with offset to prevent the page from jumping to the top
            this.renderer.setProperty(
                document.body,
                "style",
                `--scroll-y: -${this.scrollTop}px`,
            );
            this.renderer.addClass(this.document.body, "fixed-with-offset");
        } else if (this.trays.length === 0) {
            this.renderer.removeClass(document.body, "fixed-with-offset");
            // restore the previous scroll position
            windowScrollTo(window, { top: this.scrollTop });
        }
    }
}
